import { ActionIcon, Badge, Button, Group, Table, Title } from "@mantine/core";
import { IconEdit, IconPlus } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate, Outlet, useMatchRoute } from "@tanstack/react-router";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { usePermissionCheck } from "shared/stores/oidc";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { IpawsCertificateType, Permission } from "src/gql/graphql";
import { match } from "ts-pattern";

const EmptyRow = () => (
    <Table.Tr>
        <Table.Td ta="center" fs="italic" colSpan={6} p="xl">
            No Certificates
        </Table.Td>
    </Table.Tr>
);

const Page = () => {
    const nav = Route.useNavigate();
    const canEdit = usePermissionCheck(Permission.CustomerIpawsManage);

    const certs = useSuspenseQuery(queries.certs);
    const rows = certs.data.ipawsCertificates
        .toSorted((a, b) => a.name.localeCompare(b.name))
        .map((cert) => (
            <Table.Tr key={cert.id}>
                <Table.Td>{cert.name}</Table.Td>
                <Table.Td>
                    <Badge
                        color={match(cert.type)
                            .with(IpawsCertificateType.Live, () => "red")
                            .with(IpawsCertificateType.Test, () => undefined)
                            .exhaustive()}
                    >
                        {cert.type}
                    </Badge>
                </Table.Td>
                <Table.Td>
                    {new Date(parseInt(cert.expireAt)).toLocaleDateString(undefined, { month: "2-digit", day: "2-digit", year: "2-digit" })}
                </Table.Td>
                <Table.Td>{cert.active ? <Badge color="green">Ready</Badge> : <Badge color="red">Not Ready</Badge>}</Table.Td>
                <Table.Td>{cert.enabled ? <Badge color="green">Yes</Badge> : <Badge color="red">No</Badge>}</Table.Td>
                {canEdit && (
                    <Table.Td>
                        <ActionIcon
                            variant="subtle"
                            aria-label="Edit Certificate"
                            onClick={() => void nav({ to: "/ipaws/certificates/$certId", params: { certId: cert.id } }).catch(notify.catch)}
                        >
                            <IconEdit size={16} />
                        </ActionIcon>
                    </Table.Td>
                )}
            </Table.Tr>
        ));

    return (
        <>
            <Outlet />
            <Content paper style={{ overflowX: "auto" }}>
                <Content.Heading>
                    <Title order={3} size="h1">
                        Certificates
                    </Title>
                </Content.Heading>
                <Group w="100%" justify="flex-end">
                    <Link to="/ipaws/certificates/create">
                        <Button variant="default" leftSection={<IconPlus size={16} />}>
                            Add Certificate
                        </Button>
                    </Link>
                </Group>
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th miw={100}>Name</Table.Th>
                            <Table.Th>Environment</Table.Th>
                            <Table.Th>Expiration</Table.Th>
                            <Table.Th miw={100}>Status</Table.Th>
                            <Table.Th>Enabled</Table.Th>
                            {canEdit && <Table.Th>Edit</Table.Th>}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{rows.length > 0 ? rows : <EmptyRow />}</Table.Tbody>
                </Table>
            </Content>
        </>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/ipaws/certificates")({
    component: () => {
        const matchRoute = useMatchRoute();
        const canEdit = usePermissionCheck(Permission.CustomerIpawsManage);
        const canView = usePermissionCheck(Permission.IpawsProfileView);

        if (!canEdit && !canView) return <Navigate to="/" />;

        if (matchRoute({ to: "/ipaws/certificates/create" }) || matchRoute({ to: "/ipaws/certificates/$certId/details" })) {
            return <Outlet />;
        }
        return <Page />;
    },
});

const queries = {
    certs: queryOptions({
        queryKey: ["ipaws", "certifications", "table"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query IpawsCertificatesTable {
                        ipawsCertificates {
                            id
                            name
                            type
                            expireAt
                            active
                            enabled
                        }
                    }
                `),
                {},
            ),
    }),
};
